import React, { PropsWithChildren, useContext, useMemo } from "react";

import { useHistory } from "react-router-dom";

import * as browserUtils from "../utils/browserUtils";

type browserShape = {
  redirectTo: $TSFixMeFunction;
  navigateTo: $TSFixMeFunction;
  goBack: $TSFixMeFunction;
  redirectToExtern: $TSFixMeFunction;
  redirectToExternNewWindow: $TSFixMeFunction;
  currentUrl: $TSFixMeFunction;
  locale: $TSFixMeFunction;
  getBrowserLanguageWithoutRegion: () => string;
};

type WithBrowserProps = {
  browser: browserShape;
};

const BrowserContext = React.createContext<WithBrowserProps | null>(null);

type BrowserContextProviderProps = {
  testBrowser?: browserShape;
};

function BrowserContextProvider({
  children,
  testBrowser,
}: PropsWithChildren<BrowserContextProviderProps>) {
  const history = useHistory();
  // FIXME this a backward compatibility hack. One day we should have the browserUtils methods
  // only in this context and call them always via the browser context.
  (window as $TSFixMe).app.browserHistory = history;

  const context = useMemo(() => {
    if (testBrowser) return { browser: testBrowser };
    return { browser: browserUtils };
  }, []);

  return (
    <BrowserContext.Provider value={context}>
      {children}
    </BrowserContext.Provider>
  );
}

const withBrowser = <T extends WithBrowserProps = WithBrowserProps>(
  ChildComponent: React.ComponentType<T>,
) => {
  function ConnectedComponent(
    props: Readonly<Omit<T, keyof WithBrowserProps>>,
  ) {
    return (
      <BrowserContext.Consumer>
        {(context) => {
          // istanbul ignore next
          if (!context) {
            throw new Error(
              "withBrowser must be used inside a BrowserContextProvider",
            );
          }
          return <ChildComponent {...(props as T)} {...context} />;
        }}
      </BrowserContext.Consumer>
    );
  }
  ConnectedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;
  return ConnectedComponent;
};

const useBrowser = () => {
  const context = useContext(BrowserContext);
  // istanbul ignore next
  if (!context) {
    throw new Error("useBrowser must be used inside a BrowserContextProvider");
  }
  return context.browser;
};

export {
  type browserShape,
  BrowserContextProvider,
  withBrowser,
  BrowserContext,
  useBrowser,
};
